import {
  useDeleteEUDRDocById,
  useUpdateProcessMapByID,
} from 'api/services/processMapService';
import { useCallback, useEffect, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import {
  EudrRegulationDocument,
  EudrRegulationDocumentType,
} from '../organizations/NewOrganization/EudrRegulationSection';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useTranslation } from 'react-i18next';
import { useUI } from 'contexts/UiContext';
import { ErrorType, InputType } from 'types/genericTypes';
import { CreateProcessMapPayload, ProcessMapDto } from 'types/processesTypes';
import { GenericInput, GenericInputProps } from '../GenericInput';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FOLDER_PROCESS_MAP_EUDR_DOCUMENTS } from 'utils/eudrCommon';
import { DarwinCol, DarwinDivider } from 'components/darwin-ui';
import { Form } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { EudrSectionShell } from 'components/ui/EudrSectionShell';

type EudrRegulationFormData = {
  eudrFile: string[];
  processDocument: string[];
};

const defaultValuesEudrRegulationForm = {
  eudrFile: [''],
  processDocument: [''],
};

interface ProcessMapEudrRegulationSectionProps {
  control: Control<CreateProcessMapPayload>;
  processMapByIdData?: ProcessMapDto;
  processMapId?: string;
  isCreateForm?: boolean;
}

export const ProcessMapEudrRegulationSection = ({
  processMapByIdData,
  control,
  processMapId,
  isCreateForm,
}: ProcessMapEudrRegulationSectionProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();
  const queryClient = useQueryClient();

  const [isEudrRegulationCheckboxChecked, setIsEudrRegulationCheckboxChecked] =
    useState<boolean>(false);
  useEffect(() => {
    processMapByIdData?.eudrFiles?.length ||
    processMapByIdData?.processDocuments?.length
      ? setIsEudrRegulationCheckboxChecked(true)
      : setIsEudrRegulationCheckboxChecked(false);
  }, [
    processMapByIdData?.eudrFiles?.length,
    processMapByIdData?.processDocuments?.length,
  ]);

  const mainFormValues = useWatch({ control });

  const { mutateAsync: deleteEudrDocument } = useDeleteEUDRDocById();

  const { mutateAsync: updateProcessMap } = useUpdateProcessMapByID();

  const uploadDocument = useCallback(
    async (id: string, type: EudrRegulationDocument) => {
      try {
        await updateProcessMap({
          payload: {
            ...mainFormValues,
            [type]: [id],
          },
          companyId: selectedCompany || '',
          traceabilityId: processMapId || '',
        });
        openNotification(
          t('success'),
          t('eudr-regulation.file-upload-success'),
          'success'
        );
        queryClient.invalidateQueries({ queryKey: ['process-map-by-id'] });
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('eudr-regulation.delete-error-message'),
            'error'
          );
        }
      }
    },
    [
      mainFormValues,
      openNotification,
      processMapId,
      queryClient,
      selectedCompany,
      t,
      updateProcessMap,
    ]
  );

  const removeDocumentHandler = useCallback(
    async (eudrId: string) => {
      try {
        await deleteEudrDocument({
          companyId: selectedCompany || '',
          planId: processMapId || '',
          fileId: eudrId,
        });
        openNotification(
          t('success'),
          t('eudr-regulation.delete-success-message'),
          'success'
        );
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('eudr-regulation.delete-error-message'),
            'error'
          );
        }
      }
    },
    [deleteEudrDocument, openNotification, processMapId, selectedCompany, t]
  );

  const eudrInputs: Omit<
    GenericInputProps<EudrRegulationFormData>,
    'control' | 'errors'
  >[] = [
    {
      inputType: InputType.UPLOAD,
      fieldName: 'eudrFile',
      label: t('eudr-regulation.process-documents'),
      inputRules: {},
      tooltipIcon: <InfoCircleOutlined />,
      tooltipText: t('eudr-regulation.process-documents-tooltip'),
      documentHandler: uploadDocument,
      removeDocumentHandler: removeDocumentHandler,
      type: EudrRegulationDocumentType.eudrFile as EudrRegulationDocument,
      listedDocuments: processMapByIdData?.eudrFiles || [],
      folder: FOLDER_PROCESS_MAP_EUDR_DOCUMENTS,
    },
    {
      inputType: InputType.UPLOAD,
      fieldName: 'processDocument',
      label: t('eudr-regulation.files'),
      inputRules: {},
      tooltipIcon: <InfoCircleOutlined />,
      tooltipText: t('eudr-regulation.files-tooltip'),
      documentHandler: uploadDocument,
      removeDocumentHandler: removeDocumentHandler,
      type: EudrRegulationDocumentType.processDocument as EudrRegulationDocument,
      listedDocuments: processMapByIdData?.processDocuments || [],
      folder: FOLDER_PROCESS_MAP_EUDR_DOCUMENTS,
    },
  ];

  const {
    control: eudrFormControl,
    reset,
    formState: { errors: eudrFormErrors },
  } = useForm<EudrRegulationFormData>({
    defaultValues: {
      ...defaultValuesEudrRegulationForm,
    },
    mode: 'onChange',
  });

  return (
    <>
      <DarwinCol span={12} />
      <Form layout="vertical" style={{ width: '100%' }}>
        <DarwinCol span={24}>
          <GenericInput<EudrRegulationFormData>
            key={eudrInputs[0].fieldName}
            control={eudrFormControl}
            errors={eudrFormErrors}
            {...eudrInputs[0]}
          />
        </DarwinCol>
        <DarwinCol span={24}>
          <EudrSectionShell
            title={t('eudr-regulation.title')}
            checked={isEudrRegulationCheckboxChecked}
            onCheckChange={checked => {
              if (!checked) {
                setIsEudrRegulationCheckboxChecked(false);
                reset(defaultValuesEudrRegulationForm);
              } else {
                setIsEudrRegulationCheckboxChecked(true);
              }
            }}
            checkboxLabel={t('eudr-regulation.checkbox-title')}
            checkboxTooltip={t('eudr-regulation.tooltip-text')}
            checkboxDisabled={isCreateForm}
          >
            <DarwinDivider />
            <DarwinCol>
              <GenericInput<EudrRegulationFormData>
                key={eudrInputs[1].fieldName}
                control={eudrFormControl}
                errors={eudrFormErrors}
                {...eudrInputs[1]}
              />
            </DarwinCol>
          </EudrSectionShell>
        </DarwinCol>
      </Form>
    </>
  );
};
